import { includes } from "lodash";
import {
  AbstractDate,
  DateMath,
  Period,
  PeriodRange,
} from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Timezone } from "@linkfluence/models/timezone";
import { getDateFromDateMath } from "@tootools/date";
import { getLegacyFacetFromField, getLegacySandboxValue } from "../facets";
import { getFieldFromInnerClause } from "./getter";
import {
  isCustomFieldsClause,
  isDateRangeClause,
  isInfluencersTagsClause,
  isNotClause,
  isNotTermClause,
  isRangeClause,
  isSandboxedClause,
  isTermClauseOrNotTermClause,
} from "./is";
import { Clause, InnerClause, NotTermClause, TermClause } from "./model";

export function transformClauseFromField(
  clause: InnerClause,
  customFields?: string[]
) {
  const field = getFieldFromInnerClause(clause);
  if (includes(customFields, field)) {
    return transformToCustomFieldClause(clause);
  } else {
    return clause;
  }
}

export function transformToCustomFieldClause(clause: InnerClause) {
  if (isTermClauseOrNotTermClause(clause)) {
    return transformTermClauseOrNotTermClauseToCustomFieldClause(clause);
  }
  return clause;
}

function transformTermClauseOrNotTermClauseToCustomFieldClause(
  clause: NotTermClause | TermClause
) {
  if (isNotTermClause(clause)) {
    return {
      not: {
        customFields: clause.not,
      },
    };
  }
  return {
    customFields: clause,
  };
}

export function transformClauseToLegacyQueryString(
  clause: Clause,
  userTags: { id: number; label: string }[] = []
): string {
  if (!clause || clause.and.length === 0) {
    return "";
  }
  const base = "?filters=flag.rt:false,retweeters:false";
  const fields = {};
  clause.and.forEach((innerClause) => {
    const [key, value] = Object.entries(innerClause).flat();
    const legacyKey = getLegacyFacetFromField(key);
    if (isNotClause(innerClause)) {
      const notData = Object.entries(value);
      notData.forEach(([notKey, notValue]) => {
        const legacyNotKey = getLegacyFacetFromField(notKey);
        if (Array.isArray(notValue)) {
          if (notValue.length > 0) {
            const excludedValues: string = notValue
              .reduce((acc: string[], v: string | number) => {
                if (legacyNotKey === Facet.InfluencersTags) {
                  const match = userTags.find((userTag) => userTag.id === v);
                  if (match && match.label) {
                    acc.push(`-${match.label}`);
                  }
                } else {
                  acc.push(`-${v}`);
                }
                return acc;
              }, [])
              .join(";");
            if (legacyNotKey in fields) {
              if (fields[legacyNotKey].length === 0) {
                return (fields[legacyNotKey] = excludedValues);
              }
              return (fields[legacyNotKey] += `;${excludedValues}`);
            }
            return (fields[legacyNotKey] = excludedValues);
          }
        } else if (legacyNotKey === "customFields") {
          const notCustomData = Object.entries(notValue);
          notCustomData.forEach(([notCustomKey, notCustomValue]) => {
            if (Array.isArray(notCustomValue)) {
              const excludedCustomValues: string = notCustomValue
                .map((v: string) => `-${v}`)
                .join(";");
              if (notCustomKey in fields) {
                return (fields[notCustomKey] += `;${excludedCustomValues}`);
              }
              return (fields[notCustomKey] = excludedCustomValues);
            }
          });
        }
      });
    } else if (isCustomFieldsClause(innerClause)) {
      const customData = Object.entries(value);
      customData.forEach(([customKey, customValue]) => {
        if (Array.isArray(customValue)) {
          if ((customKey as string) in fields) {
            return (fields[customKey as string] += `;${customValue.join(",")}`);
          }
          if (customValue.length > 0) {
            return (fields[customKey as string] = customValue.join(","));
          }
        }
      });
    } else if (isRangeClause(innerClause)) {
      const min: string = value.gt;
      const max: string = value.lt;
      const separator =
        typeof min === "number" && typeof max === "number" ? ";" : "";
      fields[legacyKey] = `${min ?? ""}${separator}${max ?? ""}`;
    } else if (isDateRangeClause(innerClause)) {
      const max: string = value.lt?.replace(/now-|y/gi, "");
      const min: string = value.gt?.replace(/now-|y/gi, "");
      const separator =
        typeof min === "string" && typeof max === "string" ? ";" : "";
      fields[legacyKey] = `${max ?? ""}${separator}${min ?? ""}`;
    } else if (isInfluencersTagsClause(innerClause)) {
      value.forEach((tag: number) => {
        const match = userTags.find((userTag) => userTag.id === tag);
        if (match && match.label) {
          if (legacyKey in fields) {
            fields[legacyKey] += `;${match.label}`;
          } else {
            fields[legacyKey] = match.label;
          }
        }
      });
    } else if (isSandboxedClause(innerClause)) {
      const sanboxedField = getFieldFromInnerClause(innerClause);
      const sandboxedValue = getLegacySandboxValue(sanboxedField);
      if (legacyKey in fields) {
        fields[legacyKey] = `${String(fields[legacyKey])};${sandboxedValue}`;
      } else {
        fields[legacyKey] = sandboxedValue;
      }
    } else {
      fields[legacyKey] = value.toString().replace(/,/gi, ";");
    }
  });
  const clauseString = Object.entries(fields).reduce((acc, [key, value]) => {
    const res = `${key}:${value as string},`;
    return (acc += res);
  }, `${base},`);
  return clauseString
    .substring(0, clauseString.length - 1)
    .replace(/\s/g, "%20"); // %20 represents an encoded space for the url
}

function formatLegacyDate(
  date: string,
  timezone: Timezone,
  isTo?: boolean
): string {
  if (!date) {
    return new Date().toISOString().split("T")[0];
  }
  const classicDate = getDateFromDateMath(date, timezone).format();
  const [day, time] = classicDate.split("T");
  const formatedTime = time.split(":").slice(0, 2).join("-"); // 23-59
  return `${day}-${formatedTime}`; // 2023-1-1-23-59
}

function formatLimits(limits: PeriodRange, timezone: Timezone): string {
  const getClassicDate = (date: AbstractDate) =>
    getDateFromDateMath(date, timezone).format();
  if (limits) {
    const from = getClassicDate(limits.from).split("T")[0];
    const to = getClassicDate(limits.to).split("T")[0];
    return `from:${from},to:${to}`;
  } else {
    const today = new Date();
    const hundredDaysAgo = new Date(today.setDate(today.getDate() - 100));
    const from = getClassicDate(hundredDaysAgo).split("T")[0];
    const to = getClassicDate(new Date()).split("T")[0];
    return `from:${from},to:${to}`;
  }
}

function getLegacyInterval(period: Period): "days" | "hours" {
  const from = new Date(period.from);
  const to = new Date(period.to);
  const fromHour = from.getHours();
  const toHour = to.getHours();
  if (fromHour === 0 && toHour === 23) {
    return "days";
  }
  return "hours";
}

export function transformPeriodToLegacyRange(
  period: Period,
  timezone: Timezone,
  limits: PeriodRange
): string {
  let periodFrom = period?.from;
  let periodTo = period?.to;
  // If no period is specified, the last 30 days are returned by default.
  if (!period) {
    const today = new Date();
    periodFrom = new Date(
      new Date().setDate(today.getDate() - 30)
    ).toISOString() as DateMath; // thirty days ago
    periodTo = today.toISOString() as DateMath;
  }
  const from = formatLegacyDate(periodFrom, timezone);
  const to = formatLegacyDate(periodTo, timezone);
  const _limits = formatLimits(limits, timezone);
  const interval = getLegacyInterval(period);
  return `interval:${interval}&range=from:${from},to:${to}&limit=${_limits},interval:${interval}`;
}

export function getLegacyUrlQuerySeparator(
  hasFilter: boolean,
  hasPeriod: boolean
): string {
  if (hasFilter && hasPeriod) return "&";
  if (!hasFilter && hasPeriod) return "?";
  return "";
}
